/**
 * O status escolhido exige justificativa que ainda não foi escolhida?
 *
 * ## Por que isto virou um arquivo
 *
 * Em 28/10/2026 o Daniel não conseguiu abrir um chamado interno: clicou em
 * "Criar" e o sistema respondeu que faltava a **justificativa** -- um campo
 * que, nas palavras dele, "nem está visível".
 *
 * Ele tinha razão, e o motivo é de desenho: status e justificativa moram num
 * controle só (`StatusJustificationPicker`). Escolher um status que exige
 * justificativa fecha a lista e deixa o campo mostrando o nome do status, com
 * cara de preenchido. A exigência existe, mas não tem onde aparecer -- só
 * reaparece como erro, depois, em cima de um campo que parece completo.
 *
 * A regra estava escrita dentro da `TicketDetails`, a 3.200 linhas de
 * distância do componente que precisa dela. Aqui ela fica num lugar só: o
 * picker usa para **pedir** a justificativa na hora, e a tela usa para
 * **barrar** o salvamento. Duas cópias divergiriam, e a divergência seria
 * exatamente o defeito de novo -- a tela barrando algo que o campo não pediu.
 */

/** O status, dentro da lista cadastrada, ou `null`. */
export const statusPorId = (statuses, statusId) => {
    if (statusId == null || statusId === '') return null;
    return (Array.isArray(statuses) ? statuses : []).find(
        (s) => Number(s?.id) === Number(statusId)
    ) || null;
};

/** Um valor de substatus que realmente foi escolhido. */
export const temJustificativa = (substatusId) => substatusId != null && substatusId !== '';

/**
 * As justificativas cadastradas para este status.
 *
 * Status marcado como "exige justificativa" mas SEM nenhuma cadastrada é um
 * erro de configuração, não do operador: nesse caso não há o que escolher, e
 * quem chama precisa saber disso para não prender a pessoa num pedido
 * impossível de atender.
 */
export const justificativasDoStatus = (status) =>
    Array.isArray(status?.substatuses) ? status.substatuses : [];

/**
 * Falta escolher a justificativa deste status?
 *
 * @param {Array} statuses      os status cadastrados (com `substatuses`)
 * @param {number|string} statusId
 * @param {number|string} substatusId
 * @returns {boolean}
 */
export const faltaJustificativa = (statuses, statusId, substatusId) => {
    const status = statusPorId(statuses, statusId);
    if (!status || status.requires_justification !== true) return false;
    return !temJustificativa(substatusId);
};

/**
 * Dá para pedir a justificativa agora, dentro da lista aberta?
 *
 * Só quando o status exige, ainda não tem uma escolhida E existe alguma
 * cadastrada para escolher.
 */
export const podePedirJustificativa = (statuses, statusId, substatusId) => {
    if (!faltaJustificativa(statuses, statusId, substatusId)) return false;
    return justificativasDoStatus(statusPorId(statuses, statusId)).length > 0;
};

/**
 * O aviso que fica embaixo do campo, ou string vazia.
 *
 * O texto nomeia o status de propósito: o campo fechado mostra o nome dele, e
 * um aviso genérico ("preencha a justificativa") ao lado de um campo que
 * parece preenchido é justamente o que confundiu quem relatou.
 */
export const avisoDeJustificativa = (statuses, statusId, substatusId) => {
    if (!faltaJustificativa(statuses, statusId, substatusId)) return '';

    const status = statusPorId(statuses, statusId);

    if (justificativasDoStatus(status).length === 0) {
        return `O status "${status.name}" exige justificativa, mas nenhuma foi cadastrada. Avise quem administra o sistema.`;
    }

    return `O status "${status.name}" exige uma justificativa. Abra a lista e escolha uma.`;
};
